import React, { useEffect, useMemo, useState } from 'react';
import { Card } from 'primereact/card';
import { Toast } from 'primereact/toast';
import { Icon } from '@iconify/react';
import { useAuth } from '../contexts/AuthContext';
import { useCategory } from '../contexts/CategoryContext';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { productNav } from '../components/NavigationPath';
import Loader from '../components/Loader';
import Layout2 from '../components/Layout2';
import PageHeader from '../components/PageHeader';
import AddCategory from '../components/AddCategory';
import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  MouseSensor,
  KeyboardSensor,
} from '@dnd-kit/core';
import {
  SortableContext,
  rectSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { SortableItem } from './SortableItem';

function CategoryList() {
  const { toast, restaurantId, auth, hasPermission } = useAuth();
  const { getCategoriesList, getSingleCategory, updateCategoryPositions, isDraggable, setIsDraggable } = useCategory();

  const canCreate = useMemo(() => hasPermission("products", "create"),  [hasPermission]);
  const canUpdate = useMemo(() => hasPermission("products", "update"), [hasPermission]);
  const canDelete = useMemo(() => hasPermission("products", "delete"), [hasPermission]);
  const canSort = useMemo(() => hasPermission("sort-category", "access"), [hasPermission]);

  const [categoriesList, setCategoriesList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editCategoryId, setEditCategoryId] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [categoryDetails, setCategoryDetails] = useState({
    name: '',
    position: '',
    tax_rate: '18.00',
    course_id: null,
    printer_id: '',
    printer_template: '',
    notification_template: "",
    modifiers: [],
    visible: "",
    image: "",
    description: "",
    restaurant_id: restaurantId
  });

  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(KeyboardSensor)
  );

  useEffect(() => {
    if (restaurantId) {
      setIsDraggable(false);
      fetchCategories();
    }
  }, [restaurantId]);

  const fetchCategories = async () => {
    try {
      setIsLoading(true);
      const data = await getCategoriesList(restaurantId);
      if (data.categories) {
        setCategoriesList(data.categories.map((category, index) => ({
          ...category,
          id: `${category.id}`,
          order: index,
        })));
      }
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching categories', error);
    }
  };

  const handleCardClick = (category) => {
    if(!canUpdate && !canDelete) return;
    setEditMode(true);
    setEditCategoryId(category.id);
    setVisible(false);
  };

  const fetchCategoryProps = async (newCategoryId) => {
    try {
      if (editMode && editCategoryId) {
        const category = await getSingleCategory(editCategoryId);
        const categoryIndex = categoriesList.findIndex((c) => c.id === editCategoryId);

        if (categoryIndex !== -1) {
          if (!category) {
            const updatedCategoryList = [...categoriesList];
            updatedCategoryList.splice(categoryIndex, 1);
            setCategoriesList(updatedCategoryList);
          } else {
            const updatedCategoryList = [...categoriesList];
            updatedCategoryList[categoryIndex] = {...category, id: `${category?.id}`};
            setCategoriesList(updatedCategoryList);
          }
        } else {
          setCategoriesList([...categoriesList, category]);
        }
      } else if (visible) {
        const updatedCategoryList = [...categoriesList];
        updatedCategoryList.push({ ...categoryDetails, id: `${newCategoryId}`, position: categoriesList?.length });
        setCategoriesList(updatedCategoryList);
      }
    } catch (error) {
      console.error('Error fetching categories.', error);
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = categoriesList.findIndex((item) => item.id === active.id);
      const newIndex = categoriesList.findIndex((item) => item.id === over.id);

      const updatedList = arrayMove(categoriesList, oldIndex, newIndex);
      
      setCategoriesList(updatedList);

      try {
        const updatedPriorities = updatedList.map((category, index) => ({
          id: category?.id?.replace('category-', ''),
          position: index + 1,
        }));
        await updateCategoryPositions(updatedPriorities);
      } catch (error) {
        console.error('Error updating priorities:', error);
        setCategoriesList(categoriesList);
      }
    }
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  }

  const handleDragCancel = (event) => {
    setActiveId(null);
  }

  return (
    <Layout2 items={productNav.items} navTitle={productNav.header}>
      <div className="d-flex flex-column w-100">
        <PageHeader title={'Categories'} isSwitchVisible={canSort ? true : false} />
        <div className="px-4 py-2">
          {(visible || (editMode && editCategoryId)) && (
            <AddCategory
              id={editCategoryId}
              categoryVisible={visible}
              editCategoryId={editCategoryId}
              editMode={editMode}
              categoryDetails={categoryDetails}
              setEditCategoryId={setEditCategoryId}
              setCategoryVisible={setVisible}
              setEditMode={setEditMode}
              fetchCategories={fetchCategoryProps}
              setCategoryDetails={setCategoryDetails}
              canCreate={canCreate}
              canUpdate={canUpdate}
              canDelete={canDelete}
            />
          )}
          {isLoading ? (
            <Loader />
          ) : (
            <>
              <Toast ref={toast} />
              {isDraggable ? (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                  onDragCancel={handleDragCancel}
                >
                  <SortableContext
                    items={categoriesList.map((item) => item.id)}
                    strategy={rectSortingStrategy}
                  >
                    <CategoryListDisplay
                      categoriesList={categoriesList}
                      setVisible={setVisible}
                      handleCardClick={handleCardClick}
                      canCreate={canCreate}
                      canUpdate={canUpdate}
                      canDelete={canDelete}
                    />
                  </SortableContext>
                </DndContext>
              ) : (
                <CategoryListDisplay
                  categoriesList={categoriesList}
                  setVisible={setVisible}
                  handleCardClick={handleCardClick}
                  canCreate={canCreate}
                  canUpdate={canUpdate}
                  canDelete={canDelete}
                />
              )}
            </>
          )}
        </div>
      </div>
    </Layout2>
  );
}

const CategoryListDisplay = ({
  categoriesList,
  setVisible,
  handleCardClick,
  canCreate,
  canUpdate,
  canDelete
}) => {
  return (
    <div className="card-container">
      {canCreate && <Card
        title="Add Category"
        header={<Icon icon="tabler:plus" style={{ color: 'black' }} height={70} width={70} />}
        className="add-user-group"
        onClick={() => setVisible(true)}
      />}
      {categoriesList.map((category) => (
        <SortableItem key={category.id} id={category.id}>
          <Card
            title={
              <div className="product-name" title={category.name}>
                {category.name}
              </div>
            }
            header={
              category.image ? (
                <LazyLoadImage
                  src={category.image}
                  alt={category.name}
                  className="rounded-img"
                />
              ) : (
                <Icon
                  icon="ep:food"
                  className="card-icon"
                  height={100}
                  width={100}
                />
              )
            }
            className="user-list-card"
            onClick={(e) => {
              if (!e.target.closest('.sortable-item')) {
                handleCardClick(category);
              }
            }}
          />
        </SortableItem>
      ))}
    </div>
  );
};

export default CategoryList;
